import React from 'react';

import { InfoCircleIcon, StraightArrowRightIcon } from '@vlabs/icons';
import type { TPointCard } from '@vlabs/pages/point/card-page/types';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import { routes } from '@vlapps/point/src/app/routes';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, Link, useParams } from 'react-router-dom';

import st from './LinkedToCardFaceFields.module.sass';
import { useGetFace } from '@vlabs/api/luna-api/client';
import { useGetCard } from '@vlabs/api/point-api/client';

export function LinkedToCardFaceFields(): React.JSX.Element {
  const { t } = useTranslation();
  const { faceId } = useParams<{ faceId: string }>();
  const listOptions = useSelector(selectListOptions);

  const getFace = useGetFace(faceId);
  // @ts-expect-error enabled
  const getCard = useGetCard<TPointCard>(getFace.data?.external_id, undefined, {
    query: {
      enabled: !!getFace.data?.external_id,
    },
  });
  const faceData = getFace.data;
  const personCardData = getCard?.data;
  const personCardFio = personCardData?.user_data?.fio;
  const lists = faceData?.lists ?? [];

  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <div className={st.Root}>
      <div className={st.CardInfo}>
        <div className={st.CardInfoContent}>
          <span className={st.MetaLabel}>
            {t('face-page:edit_face_info.card_label')}
          </span>

          {!!personCardFio && (
            <span className={st.CardTitle}>
              {personCardFio}
            </span>
          )}

          <span className={st.MetaLabel}>
            {getFace.data?.external_id}
          </span>
        </div>

        <Control.IconButton
          as={Link}
          icon={<StraightArrowRightIcon />}
          kind="secondary"
          to={generatePath(routes.externalId.read, { externalId: getFace.data?.external_id })}
        />
      </div>

      <Control.Textarea
        data-testid="faceCreateFormTextarea"
        id="user_data"
        label={t('face-page:edit_face_info.information_label')}
        {...register('user_data', {
          maxLength: validate.maxLength(128),
        })}
        errors={errors}
      />

      <div className={st.CardListsSection}>
        <div className={st.CardListsRow}>
          <div className={st.CardListsLabel}>
            {t('face-page:edit_face_info.lists_label')}
          </div>

          <div className={st.CardLists}>
            {lists.map((listId, index) => (
              <React.Fragment key={listId}>
                {index > 0 && <span className={st.CardListsSeparator} />}
                <Control.Link
                  kind="secondary"
                  to={generatePath(routes.lists.read, { listId })}
                >
                  {listOptions?.find(({ value }) => value === listId)?.label ?? listId}
                </Control.Link>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={st.CardListsHint}>
          <InfoCircleIcon className={st.CardListsHintIcon} />
          <span>{t('face-page:edit_face_info.lists_hint')}</span>
        </div>
      </div>
    </div>
  );
}
